import { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import { Link } from "react-router-dom";

import PublicSiteHeader from "../components/PublicSiteHeader";
import AppFooter from "../components/AppFooter";
import logo from "../assets/logo_basic.png";

export default function SubmitArticlePage() {
  const [session, setSession] = useState(null);

  const [form, setForm] = useState({
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    authorName: "",
    authorTitle: "",
    authorBio: "",
    linkedin: "",
    website: "",
  });

  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadSession();
  }, []);

  const loadSession = async () => {
    const { data } = await supabase.auth.getSession();

    setSession(
      data?.session || null
    );
  };

  const makeSlug = (text) =>
    text
      .toLowerCase()
      .trim()
      .replace(
        /[^a-z0-9]+/g,
        "-"
      )
      .replace(
        /^-+|-+$/g,
        ""
      );

  const updateForm = (
    key,
    value
  ) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
      slug:
        key === "title"
          ? makeSlug(value)
          : prev.slug,
    }));
  };

  const submitArticle =
    async () => {
      setMessage("");

      if (!session?.user) {
        setMessage(
          "Please login before submitting an article."
        );

        return;
      }

      if (
        !form.title ||
        !form.excerpt ||
        !form.content ||
        !form.authorName
      ) {
        setMessage(
          "Please fill title, excerpt, content, and author name."
        );

        return;
      }

      try {
        setLoading(true);

        const user =
          session.user;

        const {
          data: author,
          error: authorError,
        } = await supabase
          .from("authors")
          .insert({
            user_id: user.id,
            name:
              form.authorName,
            title:
              form.authorTitle,
            bio:
              form.authorBio,
            linkedin:
              form.linkedin,
            website:
              form.website,
          })
          .select()
          .single();

        if (authorError) {
          throw authorError;
        }

        const {
          error: postError,
        } = await supabase
          .from("blog_posts")
          .insert({
            title:
              form.title,
            slug:
              form.slug,
            excerpt:
              form.excerpt,
            content:
              form.content,
            author_id:
              author.id,
            submitted_by:
              user.id,
            status:
              "pending",
            submitted_at:
              new Date()
                .toISOString(),
          });

        if (postError) {
          throw postError;
        }

        setMessage(
          "Article submitted successfully. It will be published after admin approval."
        );

        setForm({
          title: "",
          slug: "",
          excerpt: "",
          content: "",
          authorName: "",
          authorTitle: "",
          authorBio: "",
          linkedin: "",
          website: "",
        });
      } catch (err) {
        console.error(err);

        setMessage(
          err?.message ||
            "Unable to submit article."
        );
      } finally {
        setLoading(false);
      }
    };

  /* =====================================================
      USER NOT LOGGED IN
  ===================================================== */
  if (!session?.user) {
    return (
      <div className="min-h-screen flex flex-col bg-[#0F0A24] text-white">
        {/* SHARED PUBLIC HEADER */}
        <PublicSiteHeader
          active="blog"
        />

        <main className="flex-1 flex items-center justify-center px-6 py-16">
          <div className="max-w-xl w-full rounded-3xl border border-[#EB6AB5]/30 bg-white/5 p-8 md:p-10 text-center">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">
              Submit an Article
            </h1>

            <p className="text-white/70 mb-7 leading-relaxed">
              Please login or
              create a free
              account before
              submitting an
              article.
            </p>

            <div className="flex flex-col sm:flex-row justify-center gap-3">
              {/* LOGIN */}
              <Link
                to="/login?return=/submit-article"
                className="
                  px-6
                  py-3
                  rounded-full
                  bg-[#EB6AB5]
                  text-[#0F0A24]
                  font-bold
                  hover:bg-[#ff83d1]
                  transition
                "
              >
                Login
              </Link>

              {/* CREATE FREE ACCOUNT */}
              <Link
                to="/register?plan=free_calculator&source=article&return=/submit-article"
                className="
                  px-6
                  py-3
                  rounded-full
                  border
                  border-[#EB6AB5]/40
                  text-[#EB6AB5]
                  font-bold
                  hover:bg-[#EB6AB5]/10
                  transition
                "
              >
                Create Free Account
              </Link>
            </div>

            <div className="mt-6">
              <Link
                to="/blog"
                className="text-sm text-white/50 hover:text-[#EB6AB5] transition"
              >
                ← Back to Blog
              </Link>
            </div>
          </div>
        </main>

        {/* SAME FOOTER AS FAQ */}
        <AppFooter
          variant="landing"
          logoSrc={logo}
        />
      </div>
    );
  }

  /* =====================================================
      USER LOGGED IN
  ===================================================== */
  return (
    <div className="min-h-screen flex flex-col bg-[#0F0A24] text-white">
      {/* SHARED PUBLIC HEADER */}
      <PublicSiteHeader
        active="blog"
      />

      {/* =====================================================
          ARTICLE FORM
      ===================================================== */}
      <main className="flex-1 px-6 py-14">
        <section className="max-w-4xl mx-auto rounded-3xl border border-[#EB6AB5]/25 bg-white/5 p-6 sm:p-8 md:p-10">
          {/* PAGE HEADING */}
          <div className="mb-8">
            <p className="text-[#EB6AB5] font-bold uppercase tracking-[0.22em] text-sm mb-3">
              Career Intelligence
            </p>

            <h1 className="text-4xl font-bold text-[#EB6AB5]">
              Submit Your Article
            </h1>

            <p className="text-white/60 mt-3 max-w-2xl leading-relaxed">
              Share your career
              experience,
              research or
              practical insights
              with the Reignite
              Spirit community.
              Submitted articles
              are reviewed before
              publication.
            </p>
          </div>

          {/* ARTICLE TITLE */}
          <input
            className="
              w-full
              mb-4
              p-4
              rounded-xl
              bg-[#11111f]
              border
              border-white/10
              outline-none
              focus:border-[#EB6AB5]
              transition
            "
            placeholder="Article title"
            value={form.title}
            onChange={(e) =>
              updateForm(
                "title",
                e.target.value
              )
            }
          />

          {/* SLUG */}
          <input
            className="
              w-full
              mb-4
              p-4
              rounded-xl
              bg-[#11111f]
              border
              border-white/10
              outline-none
              focus:border-[#EB6AB5]
              transition
            "
            placeholder="URL slug"
            value={form.slug}
            onChange={(e) =>
              updateForm(
                "slug",
                e.target.value
              )
            }
          />

          {/* EXCERPT */}
          <textarea
            className="
              w-full
              min-h-[100px]
              mb-4
              p-4
              rounded-xl
              bg-[#11111f]
              border
              border-white/10
              outline-none
              focus:border-[#EB6AB5]
              transition
            "
            placeholder="Short excerpt"
            value={
              form.excerpt
            }
            onChange={(e) =>
              updateForm(
                "excerpt",
                e.target.value
              )
            }
          />

          {/* CONTENT */}
          <textarea
            className="
              w-full
              min-h-[250px]
              mb-4
              p-4
              rounded-xl
              bg-[#11111f]
              border
              border-white/10
              outline-none
              focus:border-[#EB6AB5]
              transition
            "
            placeholder="Full article content"
            value={
              form.content
            }
            onChange={(e) =>
              updateForm(
                "content",
                e.target.value
              )
            }
          />

          {/* AUTHOR PROFILE */}
          <div className="mt-8 pt-8 border-t border-white/10">
            <h2 className="text-2xl font-bold mb-2">
              Author Profile
            </h2>

            <p className="text-white/55 text-sm mb-5">
              Add the details you
              would like displayed
              with your article if
              it is approved.
            </p>

            {/* AUTHOR NAME */}
            <input
              className="
                w-full
                mb-4
                p-4
                rounded-xl
                bg-[#11111f]
                border
                border-white/10
                outline-none
                focus:border-[#EB6AB5]
                transition
              "
              placeholder="Author name"
              value={
                form.authorName
              }
              onChange={(e) =>
                updateForm(
                  "authorName",
                  e.target.value
                )
              }
            />

            {/* AUTHOR TITLE */}
            <input
              className="
                w-full
                mb-4
                p-4
                rounded-xl
                bg-[#11111f]
                border
                border-white/10
                outline-none
                focus:border-[#EB6AB5]
                transition
              "
              placeholder="Author title"
              value={
                form.authorTitle
              }
              onChange={(e) =>
                updateForm(
                  "authorTitle",
                  e.target.value
                )
              }
            />

            {/* AUTHOR BIO */}
            <textarea
              className="
                w-full
                min-h-[120px]
                mb-4
                p-4
                rounded-xl
                bg-[#11111f]
                border
                border-white/10
                outline-none
                focus:border-[#EB6AB5]
                transition
              "
              placeholder="Author bio"
              value={
                form.authorBio
              }
              onChange={(e) =>
                updateForm(
                  "authorBio",
                  e.target.value
                )
              }
            />

            {/* LINKEDIN */}
            <input
              className="
                w-full
                mb-4
                p-4
                rounded-xl
                bg-[#11111f]
                border
                border-white/10
                outline-none
                focus:border-[#EB6AB5]
                transition
              "
              placeholder="LinkedIn URL"
              value={
                form.linkedin
              }
              onChange={(e) =>
                updateForm(
                  "linkedin",
                  e.target.value
                )
              }
            />

            {/* WEBSITE */}
            <input
              className="
                w-full
                mb-4
                p-4
                rounded-xl
                bg-[#11111f]
                border
                border-white/10
                outline-none
                focus:border-[#EB6AB5]
                transition
              "
              placeholder="Website URL"
              value={
                form.website
              }
              onChange={(e) =>
                updateForm(
                  "website",
                  e.target.value
                )
              }
            />
          </div>

          {/* ACTIONS */}
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-4">
            <button
              type="button"
              onClick={
                submitArticle
              }
              disabled={loading}
              className="
                px-7
                py-3
                rounded-full
                bg-[#EB6AB5]
                hover:bg-[#ff83d1]
                disabled:opacity-60
                disabled:cursor-not-allowed
                text-[#0F0A24]
                font-bold
                transition
              "
            >
              {loading
                ? "Submitting..."
                : "Submit for Approval"}
            </button>

            <Link
              to="/blog"
              className="
                px-6
                py-3
                rounded-full
                border
                border-white/15
                text-white/70
                text-center
                hover:border-[#EB6AB5]
                hover:text-white
                transition
              "
            >
              Cancel / Back to Blog
            </Link>
          </div>

          {/* MESSAGE */}
          {message && (
            <div className="mt-5 rounded-xl border border-[#EB6AB5]/25 bg-[#EB6AB5]/5 p-4">
              <p className="text-[#EB6AB5]">
                {message}
              </p>
            </div>
          )}
        </section>
      </main>

      {/* =====================================================
          SAME FOOTER AS FAQ / BLOG
      ===================================================== */}
      <AppFooter
        variant="landing"
        logoSrc={logo}
      />
    </div>
  );
}